<template>
  <div class="blog-authors">
    <HeroSection
      title="Learn from the Best Blog Authors"
      subtitle="Our instructors are world-class professionals with significant industry experience."
      button-title="Explore our blog"
      button-link="/blog/"
      image-url="blog-authors.jpg"
      class-name="left-shadow center-gradient"
      class="authors-hero"
    />
    <div class="content">
      <div class="container">
        <h2>Meet our Blog Authors</h2>
        <div class="authors row">
          <template v-for="(author, idx) in authors">
            <div v-if="author.name" :key="idx" class="author-card col col-3 sm-6">
              <nuxt-link class="front-cover" :to="`/blog/authors/${author.authorSlug}/`">
                <div class="inner-container">
                  <div class="author-image">
                    <img
                      loading="lazy"
                      :src="`${$config.imagesUrl}/team/thumb@360_${author.image}`"
                      :alt="author.name"
                    >
                  </div>
                  <div class="info">
                    <p class="name h6">
                      {{ author.name }}
                    </p>
                    <p class="title body-2">
                      {{ author.authorTitle }}
                    </p>
                  </div>
                </div>
              </nuxt-link>
            </div>
          </template>
          <div class="col col-3 sm-6">
            <div class="author-card join-our-team">
              <div class="front-cover">
                <span class="h5">Join Our Team of Instructors</span>
                <div class="icon-wrap">
                  <IconUserPlus />
                </div>
                <nuxt-link to="/contact-us/" class="btn-primary">
                  Send Resume
                </nuxt-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeroSection from '@itbpbg/lms-components/src/components/HeroSection';
import global from '@itbpbg/lms-components/src/mixins/global';
import IconUserPlus from '../../../assets/svg/user-plus.svg?vue-component';

export default {
  components: { HeroSection, IconUserPlus },
  mixins: [global],
  layout: 'default',
  async asyncData({ $config, route, $axios }) {
    const authors = await $axios.$get('/team/authors?authorHasPosts=1');

    const page = await $axios.$get(
      `/pages/${encodeURIComponent(route.fullPath.replace(/^\/|\/$/g, ''))}`,
    );
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.metaFullImageUrl = `${$config.siteUrl}/resources/${page.metaImage}`;

    page.created = page.created || new Date();
    page.updated = page.updated || new Date();
    page.isBlog = true;

    return {
      authors: authors.filter((a) => a.name !== 'The 365 Team'),
      page,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
      {
        key: 'ImageObject',
        values: {
          '@id': `${this.page.fullPath}#primaryimage`,
          url: this.page.metaFullImageUrl,
          width: this.page.metaImageWidth,
          height: this.page.metaImageHeight,
          caption: this.page.title,
        },
      },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [{ rel: 'canonical', href: this.canonicalURL() }],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
};
</script>

<style scoped lang="scss">
.authors-hero {
  ::v-deep {
    .container .subtitle {
      margin-bottom: var(--spacing-48);
    }
  }
}

.content {
  background: var(--main-dark);
  padding: var(--spacing-100) 0 var(--spacing-120);

  h2 {
    text-align: center;
    color: var(--main-light);
    margin-bottom: var(--spacing-36);
  }

  .authors {
    .author-card {
      margin-bottom: var(--spacing-30);
      transition: transform var(--duration) ease;

      a {
        text-decoration: none;
        display: block;
        height: 100%;

        .inner-container {
          background: var(--main-dark-shade-1);
          box-shadow: 0 16px 30px rgba(0, 12, 31, 0.08);
          border-radius: 10px;
          height: 100%;

          .author-image {
            height: 270px;
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            overflow: hidden;

            img {
              transition: transform var(--duration) ease;
              width: 100%;
              height: 100%;
              object-fit: cover;
              object-position: center;
            }
          }
        }

        .info {
          padding: var(--spacing-16) var(--spacing-24) 0;

          .name {
            margin: 0;
            margin-bottom: var(--spacing-8);
            color: var(--main-light);
          }

          .title {
            margin: 0;
            color: var(--main-light-shade-4);
          }
        }
      }

      &:hover {
        img {
          transform: scale(1.01);
        }
      }

      &.join-our-team {
        background-color: var(--main-dark-shade-2);
        border-radius: 7px;
        padding: var(--spacing-36);
        text-align: center;
        overflow: hidden;

        .front-cover {
          height: 100%;
          min-height: 300px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;

          .h5 {
            color: var(--main-light);
          }

          .icon-wrap {
            border-radius: 50%;
            background-color: #323B4B;
            width: 140px;
            height: 140px;
            display: flex;
            margin: auto;

            svg {
              margin: auto;
            }
          }

          .btn-primary {
            width: 100%;
          }
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .content {
    .authors {
      .author-card.join-our-team {
        .front-cover {
          .btn-primary {
            padding: 16px;
            font-weight: 600;
            letter-spacing: 0;
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .authors-hero {
    ::v-deep {
      .container .h2 {
        font-size: 32px;
      }
    }
  }
}
</style>
